import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { global } from "@shared/libs/Global";
import {
  applySlip,
  CalAm,
  getInitFormData,
  CalAmBulk,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { dateToString } from "@shared/utils/DateUtils";
import { showResizablePopupModal } from "@widgets/CommonModal";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { RowNode } from "ag-grid-enterprise";
import SearchPanel from "@widgets/SearchPanel";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnButton } from "@shared/components/SnButton";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import SN_SCT_SIO_REG_002_POP_001 from "./SN_SCT_SIO_REG_002_POP_001";

interface Props {
  Menuprops: menuInfo;
}

// 외주입고의뢰
const SN_SCT_SIO_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  const [selectedDocu, setSelectedDocu] = useState<string>("");

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_sct_sio_reg_002_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = () => {
    try {
      gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRow1 = (): any => {
    try {
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("alter", "수정된 내용이 있습니다.");
        return false;
      }

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        cd_cust: searchInfo.cd_cust,
        nm_cust_sort: searchInfo.nm_cust_sort,
        dt_req: today,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        cd_gp: userInfo?.cd_gp,
        cd_dept: userInfo?.cd_dept,
        fg_rcv: "1",
        fg_trns: "1",
        fg_vat: "21",
        cd_exch: "000",
        rt_exch: "1",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onAddRow = () => {
    try {
      return gridRefs.current["grid2"].current?.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRow2 = (): any => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const today = dateToString(new Date());
      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      return {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_biz: focusedNodeH.data?.cd_biz,
        no_req: focusedNodeH.data?.no_req,
        no_line: maxNoLine + 1,
        dt_dlv: today,
        fg_trns: focusedNodeH.data?.fg_trns,
        fg_vat: focusedNodeH.data?.fg_vat,
        cd_exch: focusedNodeH.data?.cd_exch,
        rt_exch: focusedNodeH.data?.rt_exch,
        yn_qt: "N",
        yn_lot: "N",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    let lineList: any[] = [];
    gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
      lineList.push(node);
    });
    for (const l of lineList) {
      if (l.data.qt_rcv > 0) {
        await showDialog("alter", "이미 입고처리된 건이 존재합니다.");
        return;
      }
    }

    gridRefs.current["grid2"].current.deleteAllRows();
    gridRefs.current["grid1"].current.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    const rowCount = gridRefs.current["grid2"].current?.getDisplayedRowCount();
    if (rowCount === 0) return;

    const focusedNodeL = gridRefs.current["grid2"].current.focusedRowNode();
    if (focusedNodeL?.data.qt_rcv > 0) {
      await showDialog("alter", "이미 입고처리된 건이 존재합니다.");
      return;
    }

    gridRefs.current["grid2"].current?.deleteSelectedRows();
    if (rowCount === 1) {
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      gridRefs.current["grid1"].current.clearBlock(focusedRowNodeH);
    }
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const rcvReqHChanges = gridRefs.current["grid1"].current.getChange();
      const rcvReqLChanges = gridRefs.current["grid2"].current.getChange();
      const headerInserts = rcvReqHChanges.insert ?? [];
      const lineInserts = rcvReqLChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        rcvReqHChanges.modifiedRowcount === 0 &&
        rcvReqLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      let hasInvalid = false;
      for (const l of lineInserts) {
        if (
          l.yn_lot === "Y" &&
          (l.no_lot === null || l.no_lot === undefined || l.no_lot === "")
        ) {
          hasInvalid = true;
          break;
        }
      }
      if (hasInvalid) {
        await showDialog(
          "alter",
          "LOT관리 품목의 LOT번호가 입력되지 않았습니다."
        );
        return false;
      }

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const noSlip = await applySlip({
        cd_column: "no_req",
        cd_module: Menuprops.cd_module,
        no_line: 3,
        cd_prefix: "",
        dt: focusedRowNodeH.data.dt_req,
        girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
        inserts: [rcvReqHChanges, rcvReqLChanges],
        cd_menu: Menuprops.cd_menu,
      });

      const saveData = {
        rcvReqH: rcvReqHChanges,
        rcvReqL: rcvReqLChanges,
      };
      const ok = await fnSave("SN_SCT_SIO_REG_002_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid2"].current.resetUpdate(true);
        gridRefs.current["grid1"].current.resetUpdate(true);
        return false;
      }

      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid1"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnApply") {
      const isuReqHChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (isuReqHChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const searchInfo = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: searchInfo.cd_biz,
        cd_cust: searchInfo.cd_cust,
        dt_from: searchInfo.dt_from,
        dt_to: searchInfo.dt_to,
      };

      const component = (
        <SN_SCT_SIO_REG_002_POP_001 selectedData={selectedData} />
      );
      const result = await showResizablePopupModal({
        title: "발주적용 도움",
        children: component,
        validation: true,
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult = result[1];

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedRowNodeH && headerResult[0]) {
        focusedRowNodeH.setDataValue(
          "fg_trns",
          headerResult[0].fg_trns,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "fg_vat",
          headerResult[0].fg_vat,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "cd_cust",
          headerResult[0].cd_cust,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "nm_cust_sort",
          headerResult[0].nm_cust_sort,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "rt_exch",
          headerResult[0].rt_exch,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "cd_exch",
          headerResult[0].cd_exch,
          "noEvent"
        );
        gridRefs.current["grid1"].current.applyBlock(focusedRowNodeH);
      }
      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            addedNode.setDataValue("cd_item", result.cd_item, "noEvent");
            addedNode.setDataValue("nm_item", result.nm_item, "noEvent");
            addedNode.setDataValue(
              "dc_item_spec",
              result.dc_item_spec,
              "noEvent"
            );
            addedNode.setDataValue(
              "dc_item_unit",
              result.dc_item_unit,
              "noEvent"
            );
            addedNode.setDataValue("no_po", result.no_po, "noEvent");
            addedNode.setDataValue("no_line_pol", result.no_line, "noEvent");
            addedNode.setDataValue("qt", result.qt_app, "noEvent");
            //addedNode.setDataValue("qt_rcv", result.qt_rcv, "noEvent");
            addedNode.setDataValue("um", result.um, "noEvent");
            addedNode.setDataValue("am", result.am, "noEvent");
            addedNode.setDataValue("um_k", result.um_k, "noEvent");
            addedNode.setDataValue("am_k", result.am_k, "noEvent");
            addedNode.setDataValue("cd_pjt", result.cd_pjt, "noEvent");
            addedNode.setDataValue("nm_pjt", result.nm_pjt, "noEvent");
            addedNode.setDataValue("yn_lot", result.yn_lot, "noEvent");
            addedNode.setDataValue("yn_qc", result.yn_qc, "noEvent");
            addedNode.setDataValue("st_qc", result.st_qc, "noEvent");
          }
        }
      });
      CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "rt_exch"
      );
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_req = selectedRow.no_req;
      setSelectedDocu(no_req);
    }
  };

  const onCellValueChanged1 = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "rt_exch") {
      const rt_exch = Number(rowData.rt_exch ?? 1);
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        if (node) {
          node.setDataValue("rt_exch", rt_exch);
        }
      });
    }
    if (colId === "cd_wh") {
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        node.setDataValue("cd_wh", rowData.cd_wh);
        node.setDataValue("nm_wh", rowData.nm_wh);
      });
    }
    CalAmBulk(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const onCellValueChanged2 = async (event: CellValueChangedEvent) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    if (colId === "cd_pjt") {
      const rowCount = gridRefs.current["grid2"].current.getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.cd_pjt) return;

      let apply = false;
      gridRefs.current["grid2"].current?.forEachFilteredNode(
        (node: RowNode) => {
          if (
            node.data[colId] === "" ||
            node.data[colId] === null ||
            node.data[colId] === undefined
          )
            apply = true;
        }
      );
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );

      if (confirm) {
        gridRefs.current["grid2"].current?.forEachFilteredNode(
          (node: RowNode) => {
            if (
              node.data.cd_pjt === "" ||
              node.data.cd_pjt === null ||
              node.data.cd_pjt === undefined
            ) {
              node.setDataValue("cd_pjt", rowData.cd_pjt);
              node.setDataValue("nm_pjt", rowData.nm_pjt);
            }
          }
        );
      }
    }

    CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const setFooterDataToRow = () => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const footerData = gridRefs.current["grid2"].current.getFooterData();
    if (focusedNodeH) {
      focusedNodeH.setDataValue("am", footerData.am);
      focusedNodeH.setDataValue("am_k", footerData.am_k);
      focusedNodeH.setDataValue("am_vat", footerData.am_vat);
      focusedNodeH.setDataValue("am_supply", footerData.am_supply);
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onCellValueChanged: onCellValueChanged1,
          onBeforeAddRow: onBeforeAddRow1,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRow2,
          onBtnDeleteClick: onGrid2BtnsClick,
          onCellValueChanged: onCellValueChanged2,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "의뢰정보" }}
            events={grids.grid1.events}
            header={
              <div>
                <SnButton
                  text={"발주적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btnApply");
                  }}
                />
              </div>
            }
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_req"] }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "single", title: "상세정보" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_SCT_SIO_REG_002;
